import { useCallback } from 'react';
import { DataListItem, HeaderListItem } from '../types';
import { ROW_TYPES } from '../tableConfigs';
import { getCopyText, serializeId } from 'utils';
import { TeamMembershipType } from 'types';
import { Tenant } from 'TenantModule/types';
import { useAccountImpersonationMenu } from 'TeamModule/components/ImpersonateAccountMenu/useAccountImpersonationMenu';
import { useTeamMemberInfoModal } from 'TeamModule/components/MemberInfoModal/useTeamMemberInfoModal';
import useTeams from 'hooks/useTeams';
import useNavigationContext from 'hooks/useNavigationContext';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';

export const useTeamMemberListItemsBuilder = ({ teamId }: { teamId: number }) => {
  const { openAccountImpersonationMenu } = useAccountImpersonationMenu();
  const { openTeamMemberInfoModal } = useTeamMemberInfoModal();

  const onRowEditClick = useCallback(
    ({ accountId, event }: { accountId: number; event: React.MouseEvent }) => {
      event.stopPropagation();
      openTeamMemberInfoModal({ accountId, teamId });
    },
    [openTeamMemberInfoModal, teamId],
  );
  // Use navigation context (URL params > global state) as source of truth for tenant/realm
  const navigationContext = useNavigationContext();
  const { getSandbox } = useSandboxes();
  const { currentTeam } = useTeams({
    shouldAutoLoad: false,
  });

  // Get realm object from sandbox cache for getCopyText display formatting
  const realm = navigationContext.realmId ? getSandbox(navigationContext.realmId) : undefined;

  const listItemBuilder = useCallback(
    ({ teamMemberships }: { teamMemberships: TeamMembershipType[] }) => {
      if (!currentTeam || !currentTeam.id) {
        return [];
      }
      const validTeam = currentTeam;

      const headerListItem = {
        id: ROW_TYPES.HEADER,
        rowType: ROW_TYPES.HEADER,
        disableRowHover: false,
        isStickyHeader: true,
      } as HeaderListItem;

      const formattedAccounts: DataListItem[] = teamMemberships.map((member) => {
        const onImpersonate: DataListItem['onImpersonate'] = (e) => {
          openAccountImpersonationMenu({
            target: e.currentTarget,
            accountId: member.account_id,
            teamId,
            tags: validTeam.tags || [],
            // Use navigation context for impersonation (priority: URL params > auth defaults)
            // If undefined, navigateToMosaicAppImpersonated falls back to ApiClientManager.getAuthDefaultRealmId()
            tenant: navigationContext.tenant,
            realmId: navigationContext.realmId,
          });
        };

        const textToCopy = getCopyText({
          tenant: navigationContext.tenant
            ? ({ tenantName: navigationContext.tenant } as Tenant)
            : undefined,
          realm,
          team: validTeam,
          email: member.email,
          accountId: member.account_id,
        });

        return {
          id: serializeId([member.account_id]),
          rowType: ROW_TYPES.DATA,
          member,
          onImpersonate,
          onRowClick: (event) => onRowEditClick({ accountId: member.account_id, event }),
          textToCopy,
        };
      });

      return [headerListItem, ...formattedAccounts];
    },
    [
      currentTeam,
      realm,
      teamId,
      onRowEditClick,
      openAccountImpersonationMenu,
      navigationContext.tenant,
      navigationContext.realmId,
    ],
  );

  return listItemBuilder;
};
